/*global _, addiesaas */
import {Vue} from '~/addiesaas'
import * as types from '../mutation-types/event-reservation'

import {parseNumber, alert, fv} from '../../lib/utils'
import { formatDate, parseDateTime, time2Min} from '../../lib/dates'

import ProviderService from '../../services/event-reservation'
import config from '../../lib/config'

export const productType = 'clubspeed_event_reservation'
const reservationProducts = _.get(addiesaas.products, `types.${productType}`)

const {
  supercharged,
  TIMEZONE: timezone,
} = config

// set this true to namespace all getters, mutations, actions etc.
const namespaced = true
export const namespace = 'EventReservation'
const rootNamespace = 'RacewayBooking'
export const absoluteNamespace = [rootNamespace, namespace].join('/')

const holdDefault = {
  id: null,
  start_at: null,
  expire_at: null,
  product_id: null,
  participants: 0,
  duration: 0,
  tracks: null
  //ignore_reservation_hold_id: null
}
export const resetHold = (hold, merge = {}) => {
  return _.merge(
    {},
    holdDefault,
    _.omit(
      hold || {},
      ['id', 'expire_at', 'participants', 'duration', 'tracks']
    ),
    merge || {}
  )
}

// state
const state = {
  editItem: null,
  settings: _.merge({}, {supercharged}),
  allowSlotGrouping: false, // use this to group at front end by hour
  form: {
      termsAgreed: 'no',
      ageTermsAgreed: 'no',
      participants: null,
      duration: null,
      numTimeslots: 0, // number of actual timeslots to book
      quantity: 0, // quantity of reservation product based on actual duration (participants * numTimeslots)
      selectedDate: null,// DateTime.local().toISODate(),
      selectedSlots: {}, // id, start_at, expire_at, product_id, tracks, participants
      holdStartsAt: null,
      holdEndsAt: null,
      timezone,
      contact: {
        firstName: null,
        lastName: null,
        phone: null,
        email: null
      }
    },
  retrievedSlots: {},
  fetchingTimeslots: false,
  isHydrating: false,
  productGroupTitle: 'Event Reservation',
  editPageRoute: 'supercharged.event-reservation',
  cartItemType: '',
  productZone: null,
  reservationType: null,
  location: null,
  locationId: null,
}

const helpers = {
  parseRetrievedTimeslots(data, config, product) {
    const timeslots = _.reduce(data, (slots, item, key) => {
      const quantity = _.size(item) || 0
      const date = parseDateTime(key)
      const dateValue = date.valueOf()
      const datetime = formatDate(date, 'yyyy-MM-dd HH:mm:ss') //date.toString()
      const currentDate = formatDate(date, 'd MMMM yyyy')
      const time = formatDate(date, 'h:mm a')
      const time24 = formatDate(date, 'HH:mm')

      const product_id = item.product_id = product.id

      const slot = {
        product_id,
        datetime,
        dateValue,
        date: currentDate,
        time: time,
        timeAsNumber: time2Min(time),
        time24,

        quantity,
        participants: 0,

        hold: resetHold({
          start_at: key,
          product_id
        }),
        selected: false,
        processing: false
      }

      slots[time] = slot
      return slots

    }, {})

    return timeslots
  },
  prepareReservationCartItems(items, state, getters) {
    const rItem = items[0]
    if (_.isEmpty(rItem)) {
      return []
    }

    const {product, slot, duration, duration_label, quantity, participants, date, title, description, pricing, formattedDate, formattedTime} = rItem
    if (!participants || !duration) {
      return []
    }
    const componentSuffix = '-' + _.kebabCase(state.cartItemType || product.reservation_type)

    const itemOptions = {
      editable: true,
      deletable: true,
      editRoute: {name: state.editPageRoute},
      useTimer: true,
      showWaiver: true,
      componentSuffix,
      groupTitle: state.productGroupTitle,
      title,
      description,
      // ...pricing
    }

    const location_id = state.locationId
    const {id: product_id, type} = product

    const key = _.snakeCase(state.productGroupTitle)
    const uid = _.uniqueId((new Date()).valueOf() + '_')
    const id = _.filter([date, type, key, product_id, location_id, uid]).join('_')

    const count = quantity
    const hold = slot.hold
    const reservation_hold_id = hold.id
    const form = getters.input()
    const contact = helpers.getContact(form.contact)

    const options = {
      id,
      duration,
      duration_label,
      participants,
      start_at: slot.datetime,
      end_at: formatDate(slot.endAt, 'yyyy-MM-dd HH:mm:ss'),
      contact,
      location_id,
      reservation_hold_id,
      arriveBy: slot.arriveBy,
      tracks: hold.tracks || slot.tracks
    }

    const payload = {
      product_id,
      count,
      options
    }
    const attributes = {
      product,
      form,
      slot,
      participants,
      duration,
      duration_label,
      reservation_hold_id,
      formattedDate,
      formattedTime,
      pricing
    }
    const item = {
      ...itemOptions,
      attributes,
      id,
      type,
      payload,
    }
    return [item]
  },
  getContact(data) {
    data = data || {}
    return {
      first_name: data.firstName,
      last_name: data.lastName,
      phone: data.phone,
      ...(data.email ? {email: data.email}: {})
    }
  },
}

// mutations
const mutations = {
  [types.SET_EDIT_ITEM](state, item) {
    Vue.set(state, 'editItem', item)
  },
  [types.SET_INPUT](state, items) {
    _.forOwn(items, (value, key) => {
      if (key.includes('.')) {
        _.set(state.form, key, value)
      } else {
        Vue.set(state.form, key, value)
      }
    })
  },

  [types.RETRIEVING_TIMESLOTS](state, payload) {
    state.fetchingTimeslots = payload
  },
  [types.SET_RETRIEVED_TIMESLOTS](state, payload) {
    Vue.set(state, 'retrievedSlots', payload)
  },
  [types.SET_RETRIEVED_TIMESLOT](state, payload) {
    const time = payload.time
    const slot = state.retrievedSlots[time]
    if (slot) {
      Vue.set(state.retrievedSlots, time, payload)
    }else {
      // debugLog("missing retrieved slot for " + time)
    }
  },

  [types.UPDATE_RETRIEVED_TIMESLOT](state, {time, ...rest}) {
    const slot = state.retrievedSlots[time]
    if (slot) {
      _.forOwn(rest, (value, key) => {
        Vue.set(state.retrievedSlots[time], key, value)
      })
    } else {
      // debugLog("missing retrieved slot for " + time)
    }
  },
  [types.DELETE_RETRIEVED_TIMESLOT](state, {time}) {
    const slot = state.retrievedSlots[time]
    if (slot) {
      Vue.delete(state.retrievedSlots, time)
    } else {
      // debugLog("missing retrieved slot for " + time)
    }
  },

  [types.SET_SELECTED_TIMESLOT](state, {time}) {
    if (_.isEmpty(state.form.selectedSlots)) {
      Vue.set(state.form, 'selectedSlots', {})
    }
    const item = state.retrievedSlots[time]
    const selected = item.selected = (new Date()).valueOf()
    Vue.set(state.form.selectedSlots, time, item)
    Vue.set(state.retrievedSlots[time], 'selected', selected)

  },
  [types.UPDATE_SELECTED_TIMESLOT](state, {time, ...rest}) {
    if (_.isEmpty(state.form.selectedSlots)) {
      Vue.set(state.form, 'selectedSlots', {})
    }
    const slot = state.form.selectedSlots[time]
    if (slot) {
      _.forOwn(rest, (value, key) => {
        Vue.set(state.form.selectedSlots[time], key, value)
        Vue.set(state.retrievedSlots[time], key, value)
      })
    } else {
      // debugLog("missing selected slot for " + time)
    }
  },
  [types.DELETE_SELECTED_TIMESLOT](state, {time}) {
    if (_.isEmpty(state.form.selectedSlots)) {
      Vue.set(state.form, 'selectedSlots', {})
    }
    Vue.delete(state.form.selectedSlots, time)
    const slot = state.retrievedSlots[time] || {}
    const hold = resetHold(slot.hold || {})
    Vue.set(state.retrievedSlots[time], 'selected', false)
    Vue.set(state.retrievedSlots[time], 'hold', hold)
  },

  [types.DELETE_SELECTED_TIMESLOTS](state) {
    if (_.isEmpty(state.form.selectedSlots)) {
      Vue.set(state.form, 'selectedSlots', {})
    }
    _.forOwn(state.form.selectedSlots || {}, (slot, time) => {
      Vue.delete(state.form.selectedSlots, time)
      Vue.set(state.retrievedSlots[time], 'selected', false)
    })
  },
  [types.IS_HYDRATING_DATA](state, isHydrating) {
    state.isHydrating = isHydrating !== false
  },

  [types.SET_PRODUCT_ZONE](state, zone) {
    state.reservationType = zone
  },
  [types.SET_RESERVATION_TYPE](state, type) {
    state.reservationType = type
  },
  [types.SET_PRODUCT_LOCATION](state, location) {
    state.location = location
  },
  [types.SET_PRODUCT_LOCATION_ID](state, id) {
    state.locationId = id
  },
  [types.SET_PRODUCT_GROUP_TITLE](state, payload) {
    state.productGroupTitle = payload
  },
  [types.SET_EDIT_ROUTE](state, payload) {
    state.editPageRoute = payload
  },
  [types.SET_CART_ITEM_TYPE](state, payload) {
    state.cartItemType = payload
  },
}

const actions = {
  setReservationEditItem({commit}, payload) {
    commit(types.SET_EDIT_ITEM, payload)
  },
  setInput({commit}, payload) {
    commit(types.SET_INPUT, payload)
  },
  setRetrievingTimeslots({commit}, state) {
    commit(types.RETRIEVING_TIMESLOTS, state)
  },
  async retrieveTimeslots({commit, dispatch, getters}, options) {
    const {payload, parser} = options
    commit(types.RETRIEVING_TIMESLOTS, true)
    let data = {}
    try {
      data = await ProviderService.getUnavailability(payload)
    } catch (e) {
      const message = ProviderService.getFirstErrorMessage(e, 'Unable to retrieve booking slots. Please try again!')
      alert(message, {classes: 'text-danger'})
    }

    let timeslots = []
    if (!_.isNil(data)) {
      if (parser) {
        timeslots = parser(data, payload, getters.currentProduct)
      } else {
        timeslots = helpers.parseRetrievedTimeslots(data, payload, getters.currentProduct)
      }
    }

    dispatch('setRetrievedTimeslots', timeslots)
    commit(types.RETRIEVING_TIMESLOTS, false)
    return timeslots
  },

  setRetrievedTimeslots({commit}, payload) {
    commit(types.SET_RETRIEVED_TIMESLOTS, payload)
  },
  setRetrievedTimeslot({commit}, payload) {
    commit(types.SET_RETRIEVED_TIMESLOT, payload)
  },
  updateRetrievedTimeslot({commit}, payload) {
    commit(types.UPDATE_RETRIEVED_TIMESLOT, payload)
  },
  cleanRetrievedTimeslots({dispatch}) {
    dispatch('setRetrievedTimeslots', {})
  },

  selectTimeslot({commit}, {time}) {
    commit(types.SET_SELECTED_TIMESLOT, {time})
  },
  deselectTimeslot({commit}, {time}) {
    commit(types.DELETE_SELECTED_TIMESLOT, {time})
  },
  updateSelectedTimeslot({commit}, payload) {
    commit(types.UPDATE_SELECTED_TIMESLOT, payload)
  },
  updateSelectedAndRetrievedTimeslot({commit}, payload) {
    commit(types.UPDATE_RETRIEVED_TIMESLOT, payload)
    commit(types.UPDATE_SELECTED_TIMESLOT, payload)
  },
  cleanTimeslots({dispatch, getters}) {
    const timeslots = getters.selectedTimeslots
    _.forOwn(timeslots, item => {
      if (item) {
        dispatch('removeTimeslot', item)
      }
    })
    // commit(types.CLEAN_TIMESLOTS)
  },
  removeTimeslot({commit, dispatch, getters}, payload) {
    if (!payload) {
      return
    }
    const time = payload.time
    const slot = getters.selectedTimeslots[time] || {}
    const hold = slot.hold
    if (hold && hold.id) {
      ProviderService.unhold(hold)
    }
    dispatch('deselectTimeslot', {time})
  },
  startHydratingData({commit}, payload) {
    commit(types.IS_HYDRATING_DATA, payload)
  },

  setReservationType({commit}, type) {
    commit(types.SET_RESERVATION_TYPE, type)
  },
  setProductZone({commit}, zone) {
    commit(types.SET_PRODUCT_ZONE, zone)
  },
  setProductLocation({commit}, location) {
    commit(types.SET_PRODUCT_LOCATION, location)
  },
  setProductLocationId({commit}, id) {
    commit(types.SET_PRODUCT_LOCATION_ID, id)
  },
  setProductGroupTitle({commit}, payload) {
    commit(types.SET_PRODUCT_GROUP_TITLE, payload)
  },
  setEditPageRoute({commit}, payload) {
    commit(types.SET_EDIT_ROUTE, payload)
  },
  setCartItemType({commit}, payload) {
    commit(types.SET_CART_ITEM_TYPE, payload)
  },
}

// getters
const getters = {
  currentProductGroupTitle: state => state.productGroupTitle,
  currentEditPageRoute: state => state.editPageRoute,
  currentCartItemType: state => state.cartItemType,
  currentProductZone: state => state.productZone,
  currentReservationType: state => state.reservationType,
  currentProducts: state => _.filter(_.values(reservationProducts), ({reservation_type, publish_zones = []}) => {
    publish_zones = _.castArray(publish_zones || [])
    const isZone = !state.productZone || publish_zones.includes(state.productZone)
    const isType = state.reservationType === reservation_type
    return isZone && isType
  }),
  currentProductLocation: state => state.location,
  currentProductLocationId: state => state.locationId,
  currentProduct: (state, getters) => {
    return _.find(getters.currentProducts, product => {
      const location = state.locationId
      const all = product.supported_locations || []
      const productLocation = product.location_id
      return (!productLocation || !all.length) ||
        all.includes(location) ||
        (productLocation && productLocation === location)
    })
  },
  availableReservationDurations: (state, getters) => {
    return _.keyBy((getters.currentProduct || {}).durations || [], 'value')
  },
  selectedReservationDuration: (state, getters) => {
    const duration = getters.input('duration')
    return getters.availableReservationDurations[duration]
  },
  durationProducts: (state, getters) => {
    const locationId = getters.currentProductLocationId
    const durations = getters.availableReservationDurations
    const products = _.mapValues(durations, item => {
      const itemProducts = _.filter(item.product || [])
      let foundProduct = null
      if (itemProducts) {
        foundProduct = _.find(itemProducts, product => {
          if (locationId) {
            return product.location === locationId
          }
          return true
        })
      }
      return foundProduct
    })
    return products
  },
  selectedDurationProduct: (state, getters) => {
    const products = getters.durationProducts || {}
    const duration = getters.input('duration')
    return products[duration]
  },
  selectedDurationProductPrice: (state, getters) => (getters.selectedDurationProduct || {}).price,
  hasSelectedDurationProduct: (state, getters) => !!getters.selectedDurationProduct,
  totalQuantity: state => parseNumber(state.form.quantity),
  holdDefaults: state => holdDefault,
  isHydratingData: state => state.isHydrating,
  reservationEditItem: state => state.editItem,
  input(state) {
    return function (item) {
      if (!item) {
        return state.form
      }
      return _.get(state.form, item)
    }
  },
  retrievedTimeslot(state, getters) {
    return function (time) {
      return getters.retrievedTimeslots[time]
    }
  },
  selectedTimeslots: state => state.form.selectedSlots || _.pickBy(state.retrievedSlots, slot => slot.selected),
  retrievedTimeslots : state => state.retrievedSlots,
  isRetrievingTimeslots: state => state.fetchingTimeslots,
  breakupOfReservations(state, getters) {
    const slot = _.minBy(_.values(getters.selectedTimeslots), 'dateValue')
    if (!_.isEmpty(slot)) {
      const date = formatDate(parseDateTime(getters.input('selectedDate')), 'yyyy-MM-dd')
      const description = formatDate(slot.datetime, 'd MMMM, yyyy hh:mm a')
      const formattedDate = formatDate(slot.datetime, 'd MMMM, yyyy')
      const formattedTime = formatDate(slot.datetime, 'hh:mm a')
      const product = getters.currentProduct || {}
      const durationProduct = getters.selectedDurationProduct
      const {tax: tax_amount, price, title} = product

      const participants = _.toFinite(getters.input('participants'))
      const numTimeslots = _.toFinite(getters.input('numTimeslots'))
      const duration = _.toFinite(getters.input('duration'))
      const duration_label = (getters.selectedReservationDuration || {}).label
      const quantity = getters.totalQuantity //
      const rate = durationProduct ? _.toFinite(durationProduct.price) : (_.toFinite(price) * numTimeslots)
      const tax = _.round(_.toFinite(tax_amount) * quantity, 2)
      const subtotal = _.round(rate * participants, 2)
      const total = _.round(subtotal + tax, 2)

      return [{
        product,
        slot,
        participants,
        duration,
        duration_label,
        quantity,
        date,
        title,
        description,
        formattedDate,
        formattedTime,

        pricing: {
          count: participants,
          rate: _.round(rate, 2),
          tax,
          subtotal,
          total
        }
      }]
    }

    return []
  },
  proposedCartItems(state, getters) {
    return [...getters.breakupOfReservations]
  },
  proposedCart(state, getters) {
    return helpers.prepareReservationCartItems(getters.proposedCartItems, state, getters)
  },
  cartTotal(state, getters) {
    return getters.proposedCartItems.reduce((total, {amount = 0}) => total + amount, 0)
  },
}

// plugins
const plugins = []

const race = {namespaced, namespace, absoluteNamespace, state, mutations, actions, getters, plugins}
export default race
